import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Mail, Phone, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { API, WHATSAPP_NUMBER, PHONE_DISPLAY, CONTACT_EMAIL } from "@/config";
import { SERVICES } from "@/data/content";
import { useLanguage } from "@/i18n/LanguageContext";

export const EnquiryForm = ({ selectedService }) => {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [service, setService] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedService) setService(selectedService);
  }, [selectedService]);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, service });
      toast.success(t.enquiry.success);
      setForm({ name: "", phone: "", email: "", message: "" });
      setService("");
    } catch {
      toast.error(t.enquiry.error);
    } finally {
      setLoading(false);
    }
  };

  const whatsappText = encodeURIComponent(
    `Namaste ${t.brandFull}! I want to enquire about: ${service || t.enquiry.other}. ${form.message}`
  );

  return (
    <section id="contact" className="border-t border-border bg-muted/40">
      <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24">
        <div className="grid gap-12 lg:grid-cols-2">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-rose-600">{t.enquiry.contactInfo}</p>
            <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
              {t.enquiry.heading}
            </h2>
            <p className="mt-4 text-base text-muted-foreground sm:text-lg">{t.enquiry.subheading}</p>
            <div className="mt-8 space-y-5">
              <div className="flex items-start gap-3">
                <MapPin className="mt-1 h-5 w-5 text-rose-600" />
                <div>
                  <p className="text-sm font-semibold">{t.about.headOffice}</p>
                  <p className="text-sm text-muted-foreground">{t.about.headAddress}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="mt-1 h-5 w-5 text-amber-600" />
                <div>
                  <p className="text-sm font-semibold">{t.about.branchOffice}</p>
                  <p className="text-sm text-muted-foreground">{t.about.branchAddress}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="mt-1 h-5 w-5 text-rose-600" />
                <a href={`tel:${PHONE_DISPLAY.replace(/\s/g, "")}`} data-testid="contact-phone-link" className="text-sm text-muted-foreground hover:text-foreground">
                  {PHONE_DISPLAY}
                </a>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="mt-1 h-5 w-5 text-rose-600" />
                <a href={`mailto:${CONTACT_EMAIL}`} data-testid="contact-email-link" className="text-sm text-muted-foreground hover:text-foreground">
                  {CONTACT_EMAIL}
                </a>
              </div>
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            data-testid="enquiry-form"
            className="rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8"
          >
            <div className="grid gap-5">
              <div className="grid gap-2">
                <Label htmlFor="enquiry-name">{t.enquiry.name} *</Label>
                <Input
                  id="enquiry-name"
                  data-testid="enquiry-form-name-input"
                  required
                  value={form.name}
                  onChange={update("name")}
                  placeholder={t.enquiry.namePlaceholder}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="enquiry-phone">{t.enquiry.phone} *</Label>
                <Input
                  id="enquiry-phone"
                  data-testid="enquiry-form-phone-input"
                  required
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder={t.enquiry.phonePlaceholder}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="enquiry-email">{t.enquiry.email}</Label>
                <Input
                  id="enquiry-email"
                  data-testid="enquiry-form-email-input"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@example.com"
                />
              </div>
              <div className="grid gap-2">
                <Label>{t.enquiry.service} *</Label>
                <Select value={service} onValueChange={setService} required>
                  <SelectTrigger data-testid="enquiry-form-service-select">
                    <SelectValue placeholder={t.enquiry.service} />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => (
                      <SelectItem key={s.id} value={s.id} data-testid={`service-option-${s.id}`}>
                        {t.serviceItems[s.id].title}
                      </SelectItem>
                    ))}
                    <SelectItem value="other" data-testid="service-option-other">
                      {t.enquiry.other}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="enquiry-message">{t.enquiry.message}</Label>
                <Textarea
                  id="enquiry-message"
                  data-testid="enquiry-form-message-input"
                  rows={4}
                  value={form.message}
                  onChange={update("message")}
                  placeholder={t.enquiry.messagePlaceholder}
                />
              </div>
              <div className="flex flex-wrap gap-3 pt-1">
                <Button type="submit" data-testid="enquiry-form-submit-button" disabled={loading} className="gap-2">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {t.enquiry.submit}
                </Button>
                <Button asChild variant="outline" data-testid="enquiry-whatsapp-button" className="gap-2 border-emerald-600/40 text-emerald-600 hover:bg-emerald-600/10">
                  <a href={`https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappText}`} target="_blank" rel="noopener noreferrer">
                    {t.enquiry.whatsapp}
                  </a>
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};
